/**
 * Element Cards - Interactive stacked cards component
 */

const CARD_TRANSITION = 'all 0.7s cubic-bezier(0.4, 0, 0.2, 1)';

class ElementCards extends HTMLElement {
  constructor() {
    super();
    this.cards = [];
    this.dots = [];
    this.isAnimating = false;
    const autoRotateAttr = this.getAttribute('data-auto-rotate');
    this.autoRotate = autoRotateAttr !== 'false';
    this.autoRotateTimer = null;
    this.autoRotateDelay = parseInt(this.getAttribute('data-auto-rotate-delay'), 10) || 5000;
    this.isHovered = false;
    this.handleClick = this.handleClick.bind(this);
    this.handleDotKeydown = this.handleDotKeydown.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.stopAutoRotate();
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleDotKeydown);
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  init() {
    this.cards = Array.from(this.querySelectorAll('.element-card'));
    this.dots = Array.from(this.querySelectorAll('.element-cards__dot'));

    if (this.cards.length === 0) return;

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleDotKeydown);

    // Start auto-rotate if enabled
    if (this.autoRotate) {
      this.startAutoRotate();

      // Pause on hover
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
    }

    this.updateDots();
  }

  handleClick(event) {
    if (this.isAnimating) return;

    const clickedDot = event.target.closest('.element-cards__dot');
    if (clickedDot && this.contains(clickedDot)) {
      this.goToCardById(clickedDot.getAttribute('data-card-id'));
      if (this.autoRotate && !this.isHovered) {
        this.startAutoRotate();
      }
      return;
    }

    const clickedCard = event.target.closest('.element-card');
    if (!clickedCard || !this.contains(clickedCard)) return;

    const clickedIndex = parseInt(clickedCard.getAttribute('data-index'), 10);
    if (Number.isNaN(clickedIndex) || clickedIndex !== 0) return;

    this.cycleCards();
    if (this.autoRotate && !this.isHovered) {
      this.startAutoRotate();
    }
  }

  handleDotKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const focusedDot = event.target.closest('.element-cards__dot');
    if (!focusedDot || !this.contains(focusedDot)) return;

    event.preventDefault();
    this.goToCardById(focusedDot.getAttribute('data-card-id'));
    if (this.autoRotate && !this.isHovered) {
      this.startAutoRotate();
    }
  }

  handleMouseEnter() {
    this.isHovered = true;
    this.stopAutoRotate();
  }

  handleMouseLeave() {
    this.isHovered = false;
    this.startAutoRotate();
  }

  applyCardIndices() {
    this.cards.forEach((card, index) => {
      card.setAttribute('data-index', index);
      card.style.setProperty('--card-index', index);
    });
  }

  getCyclePlan(targetCardId) {
    if (!targetCardId || this.isAnimating) return null;

    const targetIndex = this.cards.findIndex(card => card.getAttribute('data-card-id') === targetCardId);
    if (targetIndex <= 0) return null;

    const forwardSteps = targetIndex;
    const backwardSteps = this.cards.length - targetIndex;

    return {
      steps: Math.min(forwardSteps, backwardSteps),
      cycleFn: forwardSteps <= backwardSteps ? this.cycleCards.bind(this) : this.cycleCardsBackward.bind(this)
    };
  }

  runCycleSteps(steps, cycleFn) {
    let remainingSteps = steps;

    const cycleToTarget = () => {
      if (remainingSteps <= 0) return;
      cycleFn(() => {
        remainingSteps -= 1;
        cycleToTarget();
      });
    };

    cycleToTarget();
  }

  goToCardById(targetCardId) {
    const plan = this.getCyclePlan(targetCardId);
    if (!plan) return;
    this.runCycleSteps(plan.steps, plan.cycleFn);
  }

  cycleCards(onComplete) {
    if (this.isAnimating || this.cards.length <= 1) {
      if (typeof onComplete === 'function') onComplete(false);
      return;
    }

    this.isAnimating = true;

    // Get the first card
    const firstCard = this.cards[0];

    // Add cycling class for animation
    firstCard.classList.add('element-card--cycling');

    // After animation completes, rearrange the DOM
    setTimeout(() => {
      // Move first card to the end
      const parent = firstCard.parentElement;
      parent.appendChild(firstCard);

      // Update cards array
      this.cards.push(this.cards.shift());

      // Update data-index attributes
      this.applyCardIndices();

      this.updateDots();

      // Remove cycling class
      firstCard.classList.remove('element-card--cycling');

      this.isAnimating = false;
      if (typeof onComplete === 'function') onComplete(true);
    }, 500);
  }

  cycleCardsBackward(onComplete) {
    if (this.isAnimating || this.cards.length <= 1) {
      if (typeof onComplete === 'function') onComplete(false);
      return;
    }

    const lastCard = this.cards[this.cards.length - 1];
    const parent = lastCard?.parentElement;
    if (!lastCard || !parent) {
      if (typeof onComplete === 'function') onComplete(false);
      return;
    }

    this.isAnimating = true;

    lastCard.style.transition = 'none';
    lastCard.style.transform = 'translateY(-2rem) scale(1.05)';
    lastCard.style.opacity = '0';
    lastCard.style.zIndex = '10';

    parent.insertBefore(lastCard, parent.firstChild);
    this.cards.unshift(this.cards.pop());

    this.applyCardIndices();

    requestAnimationFrame(() => {
      this.cards.forEach(card => {
        card.style.transition = CARD_TRANSITION;
      });

      lastCard.style.transform = '';
      lastCard.style.opacity = '1';
      lastCard.style.zIndex = '';

      setTimeout(() => {
        lastCard.style.transition = '';
        this.updateDots();
        this.isAnimating = false;
        if (typeof onComplete === 'function') onComplete(true);
      }, 500);
    });
  }

  startAutoRotate() {
    if (!this.autoRotate || this.cards.length <= 1) return;

    this.stopAutoRotate();
    this.autoRotateTimer = setTimeout(() => {
      if (!this.isHovered) {
        this.cycleCards();
      }
      this.startAutoRotate();
    }, this.autoRotateDelay);
  }

  stopAutoRotate() {
    if (this.autoRotateTimer) {
      clearTimeout(this.autoRotateTimer);
      this.autoRotateTimer = null;
    }
  }

  updateDots() {
    if (!this.dots.length || !this.cards.length) return;
    const activeCardId = this.cards[0].getAttribute('data-card-id');
    this.dots.forEach(dot => {
      const isActive = dot.getAttribute('data-card-id') === activeCardId;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

// Register custom element
if (!customElements.get('element-cards')) {
  customElements.define('element-cards', ElementCards);
}

// Alternative: Use standard JavaScript if custom elements aren't desired
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[data-section-type="element-cards"]');

  sections.forEach(section => {
    const cardsContainer = section.querySelector('.element-cards__stack');
    if (!cardsContainer) return;

    const cards = Array.from(cardsContainer.querySelectorAll('.element-card'));
    const dots = Array.from(section.querySelectorAll('.element-cards__dot'));
    const dotsContainer = section.querySelector('.element-cards__dots');
    let isAnimating = false;
    const autoRotateAttr = section.getAttribute('data-auto-rotate');
    const autoRotate = autoRotateAttr !== 'false';
    const autoRotateDelay = parseInt(section.getAttribute('data-auto-rotate-delay'), 10) || 5000;
    let autoRotateTimer = null;
    let isHovered = false;

    const updateCardIndices = () => {
      cards.forEach((card, index) => {
        card.setAttribute('data-index', index);
        card.style.setProperty('--card-index', index);
      });
    };

    const updateDots = () => {
      if (!dots.length || !cards.length) return;
      const activeCardId = cards[0].getAttribute('data-card-id');
      dots.forEach(dot => {
        const isActive = dot.getAttribute('data-card-id') === activeCardId;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const cycleCards = onComplete => {
      if (isAnimating || cards.length <= 1) {
        if (typeof onComplete === 'function') onComplete(false);
        return;
      }

      isAnimating = true;

      const firstCard = cards[0];

      // Animate first card: grow and fade out while moving to the back
      firstCard.style.transition = CARD_TRANSITION;
      firstCard.style.transform = 'translateY(-2rem) scale(1.05)';
      firstCard.style.opacity = '0';
      firstCard.style.zIndex = '10';

      // Move the card to the end of the DOM after animation starts
      setTimeout(() => {
        cardsContainer.appendChild(firstCard);
        cards.push(cards.shift());

        // Update all cards with new indices
        updateCardIndices();
        updateDots();
        cards.forEach(card => {
          card.style.transition = CARD_TRANSITION;
        });

        // Reset the first card (now at the back) without transition
        setTimeout(() => {
          firstCard.style.transition = 'none';
          firstCard.style.transform = '';
          firstCard.style.opacity = '1';
          firstCard.style.zIndex = '';

          // Re-enable transitions after reset
          setTimeout(() => {
            firstCard.style.transition = '';
            isAnimating = false;
            if (typeof onComplete === 'function') onComplete(true);
          }, 50);
        }, 100);
      }, 400);
    };

    const cycleCardsBackward = onComplete => {
      if (isAnimating || cards.length <= 1) {
        if (typeof onComplete === 'function') onComplete(false);
        return;
      }

      const lastCard = cards[cards.length - 1];
      if (!lastCard) {
        if (typeof onComplete === 'function') onComplete(false);
        return;
      }

      isAnimating = true;

      lastCard.style.transition = 'none';
      lastCard.style.transform = 'translateY(-2rem) scale(1.05)';
      lastCard.style.opacity = '0';
      lastCard.style.zIndex = '10';

      cardsContainer.insertBefore(lastCard, cardsContainer.firstChild);
      cards.unshift(cards.pop());
      updateCardIndices();

      requestAnimationFrame(() => {
        cards.forEach(card => {
          card.style.transition = CARD_TRANSITION;
        });

        lastCard.style.transform = '';
        lastCard.style.opacity = '1';
        lastCard.style.zIndex = '';

        setTimeout(() => {
          lastCard.style.transition = '';
          updateDots();
          isAnimating = false;
          if (typeof onComplete === 'function') onComplete(true);
        }, 500);
      });
    };

    const getCyclePlan = targetCardId => {
      if (!targetCardId || isAnimating) return null;

      const targetIndex = cards.findIndex(card => card.getAttribute('data-card-id') === targetCardId);
      if (targetIndex <= 0) return null;

      const forwardSteps = targetIndex;
      const backwardSteps = cards.length - targetIndex;

      return {
        steps: Math.min(forwardSteps, backwardSteps),
        cycleFn: forwardSteps <= backwardSteps ? cycleCards : cycleCardsBackward
      };
    };

    const runCycleSteps = (steps, cycleFn) => {
      let remainingSteps = steps;

      const cycleToTarget = () => {
        if (remainingSteps <= 0) return;
        cycleFn(() => {
          remainingSteps -= 1;
          cycleToTarget();
        });
      };

      cycleToTarget();
    };

    const goToCardById = targetCardId => {
      const plan = getCyclePlan(targetCardId);
      if (!plan) return;
      runCycleSteps(plan.steps, plan.cycleFn);
    };

    cardsContainer.addEventListener('click', event => {
      const clickedCard = event.target.closest('.element-card');
      if (!clickedCard || !cardsContainer.contains(clickedCard)) return;
      if (clickedCard.getAttribute('data-index') !== '0') return;
      cycleCards();
      if (autoRotate && !isHovered) {
        startAutoRotate();
      }
    });

    if (dotsContainer) {
      dotsContainer.addEventListener('click', event => {
        const clickedDot = event.target.closest('.element-cards__dot');
        if (!clickedDot || !dotsContainer.contains(clickedDot)) return;

        goToCardById(clickedDot.getAttribute('data-card-id'));
        if (autoRotate && !isHovered) {
          startAutoRotate();
        }
      });

      dotsContainer.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        const focusedDot = event.target.closest('.element-cards__dot');
        if (!focusedDot || !dotsContainer.contains(focusedDot)) return;

        event.preventDefault();
        goToCardById(focusedDot.getAttribute('data-card-id'));
        if (autoRotate && !isHovered) {
          startAutoRotate();
        }
      });
    }

    const startAutoRotate = () => {
      if (!autoRotate || cards.length <= 1) return;
      stopAutoRotate();
      autoRotateTimer = setTimeout(() => {
        if (!isHovered) {
          cycleCards();
        }
        startAutoRotate();
      }, autoRotateDelay);
    };

    const stopAutoRotate = () => {
      if (autoRotateTimer) {
        clearTimeout(autoRotateTimer);
        autoRotateTimer = null;
      }
    };

    cardsContainer.addEventListener('mouseenter', () => {
      isHovered = true;
      stopAutoRotate();
    });

    cardsContainer.addEventListener('mouseleave', () => {
      isHovered = false;
      startAutoRotate();
    });

    startAutoRotate();
    updateDots();
  });
});
